import {
  BOARD_SIZE,
  moveNotation,
  positionKey,
  progressAt,
  type GameState,
  type Move,
  type Player,
} from "../game";
import { generateLegalMovesUnchecked } from "../game/rules";
import { applyGeneratedMove } from "../game/searchTransition";
import { getDifficultyPolicy, type BotDifficulty, type SearchBudget } from "./difficulty";
import {
  evaluateState,
  HEURISTIC_SCORE_LIMIT,
  TERMINAL_SCORE,
  type TerminalAssessment,
} from "./evaluation";
import { createsForcedBackRowClimb } from "./fallback";
import { searchFastExtreme } from "./fastSearch";
import { RepetitionTracker } from "./repetitionTracker";
import {
  BoundedTranspositionTable,
  transpositionKey,
  type TranspositionBound,
} from "./transpositionTable";

const NEGATIVE_INFINITY = -TERMINAL_SCORE * 2;
const POSITIVE_INFINITY = TERMINAL_SCORE * 2;
const EXTREME_EARLY_FIELD_THRESHOLD = 23;
const EXTREME_EARLY_DEPTH = 6;
const EXTREME_INITIAL_DEPTH = 7;
const EXTREME_INITIAL_NODE_LIMIT = 580_000;
const EXTREME_CONTRACTED_NODE_LIMIT = 450_000;

export type ScoredMove = Readonly<{
  move: Move;
  score: number;
  provenTerminal: TerminalAssessment;
}>;

export type SearchResult = Readonly<{
  move: Move;
  rankedMoves: readonly ScoredMove[];
  nodes: number;
  completedDepth: number;
  exhaustedBudget: boolean;
}>;

type SearchContext = {
  readonly perspective: Player;
  readonly nodeLimit: number;
  readonly table: BoundedTranspositionTable;
  readonly repetitions: RepetitionTracker;
  nodes: number;
};

class NodeBudgetExhausted extends Error {}

type SearchValue = Readonly<{
  score: number;
  terminal: TerminalAssessment;
}>;

function validateBudget(budget: SearchBudget): void {
  const values = [
    budget.maxDepth,
    budget.nodeLimit,
    budget.tableCapacity,
    budget.candidateCount,
    budget.plausibilityWindow,
  ];
  if (
    values.some((value) => !Number.isSafeInteger(value) || value < 0) ||
    budget.maxDepth < 1 ||
    budget.nodeLimit < 1 ||
    budget.tableCapacity < 1 ||
    budget.candidateCount < 1
  ) {
    throw new RangeError("Search budgets must contain positive integer limits.");
  }
}

function moveOrderingScore(state: GameState, move: Move): number {
  if (move.kind === "passage") return TERMINAL_SCORE;
  const destination = state.cells[move.to.row * 5 + move.to.column];
  const destinationHeight = destination?.kind === "field" ? destination.stack.length : 0;
  const progressDelta =
    progressAt(state.activePlayer, move.to) - progressAt(state.activePlayer, move.from);
  const centrality = 4 - Math.abs(2 - move.to.row) - Math.abs(2 - move.to.column);
  return destinationHeight * 40 + progressDelta * 12 + centrality;
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function isInitialPosition(state: GameState): boolean {
  if (state.activePlayer !== "black" || state.outcome !== null) return false;
  return state.cells.every((cell, index) => {
    if (cell.kind !== "field") return false;
    const row = Math.floor(index / BOARD_SIZE);
    if (row === 0) return cell.stack.length === 1 && cell.stack[0] === "red";
    if (row === BOARD_SIZE - 1) return cell.stack.length === 1 && cell.stack[0] === "black";
    return cell.stack.length === 0;
  });
}

function isHorizontallySymmetric(state: GameState): boolean {
  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let column = 0; column < Math.floor(BOARD_SIZE / 2); column += 1) {
      const left = state.cells[row * BOARD_SIZE + column];
      const right = state.cells[row * BOARD_SIZE + (BOARD_SIZE - 1 - column)];
      if (left?.kind !== right?.kind) return false;
      if (
        left?.kind === "field" &&
        right?.kind === "field" &&
        (left.stack.length !== right.stack.length ||
          left.stack.some((disc, index) => disc !== right.stack[index]))
      ) {
        return false;
      }
    }
  }
  return true;
}

export function orderMoves(state: GameState, moves: readonly Move[]): readonly Move[] {
  return Object.freeze(
    [...moves].sort(
      (left, right) =>
        moveOrderingScore(state, right) - moveOrderingScore(state, left) ||
        compareText(moveNotation(left), moveNotation(right)),
    ),
  );
}

function orderRootMoves(state: GameState, moves: readonly Move[], seed: number): readonly Move[] {
  // Root alpha-beta intentionally keeps later equal bounds behind its incumbent. Put this
  // conspicuous forced-passage concession last so an equivalent alternative becomes incumbent.
  const forcedClimbByMove = new Map(
    moves.map((move) => [moveNotation(move), createsForcedBackRowClimb(state, move)]),
  );
  const ordered = [...moves].sort((left, right) => {
    const leftForcedClimb = forcedClimbByMove.get(moveNotation(left)) === true;
    const rightForcedClimb = forcedClimbByMove.get(moveNotation(right)) === true;
    return (
      Number(leftForcedClimb) - Number(rightForcedClimb) ||
      moveOrderingScore(state, right) - moveOrderingScore(state, left) ||
      compareText(moveNotation(left), moveNotation(right))
    );
  });
  if (!isHorizontallySymmetric(state)) return Object.freeze(ordered);
  let randomState = (seed ^ 0x9e37_79b9) >>> 0;
  const nextRandom = (): number => {
    randomState ^= randomState << 13;
    randomState ^= randomState >>> 17;
    randomState ^= randomState << 5;
    return randomState >>> 0;
  };

  for (let start = 0; start < ordered.length;) {
    const first = ordered[start];
    if (first === undefined) break;
    const priority = moveOrderingScore(state, first);
    const forcedClimb = forcedClimbByMove.get(moveNotation(first)) === true;
    let end = start + 1;
    while (end < ordered.length) {
      const candidate = ordered[end];
      if (
        candidate === undefined ||
        moveOrderingScore(state, candidate) !== priority ||
        (forcedClimbByMove.get(moveNotation(candidate)) === true) !== forcedClimb
      ) {
        break;
      }
      end += 1;
    }

    for (let index = end - 1; index > start; index -= 1) {
      const swapIndex = start + (nextRandom() % (index - start + 1));
      const current = ordered[index];
      const swap = ordered[swapIndex];
      if (current !== undefined && swap !== undefined) {
        ordered[index] = swap;
        ordered[swapIndex] = current;
      }
    }
    start = end;
  }

  return Object.freeze(ordered);
}

function applyLegalMove(state: GameState, move: Move): GameState {
  return applyGeneratedMove(state, move).state;
}

function consumeNode(context: SearchContext): void {
  if (context.nodes >= context.nodeLimit) throw new NodeBudgetExhausted();
  context.nodes += 1;
}

function searchChild(
  state: GameState,
  depth: number,
  alpha: number,
  beta: number,
  context: SearchContext,
): SearchValue {
  if (state.outcome !== null) return alphaBeta(state, depth, alpha, beta, context);
  const key = positionKey(state);
  if (context.repetitions.enter(key)) return Object.freeze({ score: 0, terminal: "draw" });
  try {
    return alphaBeta(state, depth, alpha, beta, context);
  } finally {
    context.repetitions.leave(key);
  }
}

function alphaBeta(
  state: GameState,
  depth: number,
  alphaInput: number,
  betaInput: number,
  context: SearchContext,
): SearchValue {
  consumeNode(context);
  if (state.outcome !== null || depth === 0) return evaluateState(state, context.perspective);

  const key = `${transpositionKey(state)}|${context.repetitions.signature(depth)}`;
  const cached = context.table.get(key);
  let alpha = alphaInput;
  let beta = betaInput;
  if (cached !== undefined && cached.depth >= depth) {
    if (cached.bound === "exact") {
      return Object.freeze({ score: cached.score, terminal: cached.terminal });
    }
    if (cached.bound === "lower") alpha = Math.max(alpha, cached.score);
    else beta = Math.min(beta, cached.score);
    if (alpha >= beta) return Object.freeze({ score: cached.score, terminal: null });
  }

  const originalAlpha = alpha;
  const originalBeta = beta;
  const maximizing = state.activePlayer === context.perspective;
  let best = maximizing ? NEGATIVE_INFINITY : POSITIVE_INFINITY;
  let bestTerminal: TerminalAssessment = null;

  for (const move of orderMoves(state, generateLegalMovesUnchecked(state))) {
    const nextState = applyLegalMove(state, move);
    const value = searchChild(nextState, depth - 1, alpha, beta, context);
    const score = value.score;
    if (maximizing) {
      if (score > best) {
        best = score;
        bestTerminal = value.terminal;
      }
      alpha = Math.max(alpha, best);
    } else {
      if (score < best) {
        best = score;
        bestTerminal = value.terminal;
      }
      beta = Math.min(beta, best);
    }
    if (alpha >= beta) break;
  }

  let bound: TranspositionBound = "exact";
  if (best <= originalAlpha) bound = "upper";
  else if (best >= originalBeta) bound = "lower";
  const terminal = bound === "exact" ? bestTerminal : null;
  context.table.set(key, { depth, score: best, bound, terminal });
  return Object.freeze({ score: best, terminal });
}

function compareScoredMoves(left: ScoredMove, right: ScoredMove): number {
  return right.score - left.score || compareText(moveNotation(left.move), moveNotation(right.move));
}

function rankAtDepth(
  state: GameState,
  moves: readonly Move[],
  depth: number,
  context: SearchContext,
  pruneRoot: boolean,
): readonly ScoredMove[] {
  const scored: ScoredMove[] = [];
  let rootAlpha = NEGATIVE_INFINITY;
  for (const move of moves) {
    const nextState = applyLegalMove(state, move);
    const value = searchChild(
      nextState,
      depth - 1,
      pruneRoot ? rootAlpha : NEGATIVE_INFINITY,
      POSITIVE_INFINITY,
      context,
    );
    const rawScore = value.score;
    // A root cutoff proves only that this later move cannot beat the first
    // stable incumbent. Keep it behind that incumbent rather than presenting
    // the returned bound as a false exact tie.
    const score = pruneRoot && rawScore <= rootAlpha ? rootAlpha - 1 : rawScore;
    rootAlpha = Math.max(rootAlpha, rawScore);
    scored.push(
      Object.freeze({
        move,
        score,
        provenTerminal: score === rawScore ? value.terminal : null,
      }),
    );
  }
  scored.sort(compareScoredMoves);
  return Object.freeze(scored);
}

function seedToIndex(seed: number, length: number): number {
  let value = seed >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return (value >>> 0) % length;
}

function selectMove(rankedMoves: readonly ScoredMove[], budget: SearchBudget, seed: number): Move {
  const winning = rankedMoves.filter((entry) => entry.provenTerminal === "win");
  if (winning.length > 0) {
    const selected = winning[seedToIndex(seed, winning.length)] ?? winning[0];
    if (selected === undefined)
      throw new Error("Winning move selection unexpectedly became empty.");
    return selected.move;
  }

  const bestScore = rankedMoves[0]?.score;
  if (bestScore === undefined) throw new Error("Cannot select a move from an empty ranking.");
  const candidates = rankedMoves
    .filter((entry) => entry.score >= bestScore - budget.plausibilityWindow)
    .slice(0, budget.candidateCount);
  const selected = candidates[seedToIndex(seed, candidates.length)] ?? candidates[0];
  if (selected === undefined)
    throw new Error("Plausible move selection unexpectedly became empty.");
  return selected.move;
}

export function searchBotMove(
  state: GameState,
  difficulty: BotDifficulty,
  seed: number,
  budget: SearchBudget = getDifficultyPolicy(difficulty).budget,
  positionKeys: readonly string[] = Object.freeze([positionKey(state)]),
  extremeTable?: BoundedTranspositionTable,
): SearchResult {
  validateBudget(budget);
  if (!Number.isSafeInteger(seed) || seed < 0 || seed > 0xffff_ffff) {
    throw new RangeError("Bot seed must be an unsigned 32-bit integer.");
  }
  if (positionKeys.length === 0 || positionKeys.at(-1) !== positionKey(state)) {
    throw new TypeError("Bot position history must end at the searched state.");
  }

  // Seeded variety is limited to equally promising root-ordering groups. Alpha-beta still
  // selects the strongest searched score, so Hard never deliberately accepts a weaker move.
  const legalMoves = orderRootMoves(state, generateLegalMovesUnchecked(state), seed);
  if (legalMoves.length === 0) throw new Error("A bot move was requested from a terminal state.");

  if (difficulty === "extreme") {
    const initialPosition = isInitialPosition(state);
    const existingFields = state.cells.reduce(
      (count, cell) => count + (cell.kind === "field" ? 1 : 0),
      0,
    );
    const maxCompletedDepth = initialPosition
      ? Math.min(budget.maxDepth, EXTREME_INITIAL_DEPTH)
      : existingFields >= EXTREME_EARLY_FIELD_THRESHOLD
        ? Math.min(budget.maxDepth, EXTREME_EARLY_DEPTH)
        : budget.maxDepth;
    const searchBudget =
      initialPosition || existingFields < EXTREME_EARLY_FIELD_THRESHOLD
        ? Object.freeze({
            ...budget,
            nodeLimit: Math.min(
              budget.nodeLimit,
              initialPosition ? EXTREME_INITIAL_NODE_LIMIT : EXTREME_CONTRACTED_NODE_LIMIT,
            ),
          })
        : budget;
    const result = searchFastExtreme(
      state,
      legalMoves,
      searchBudget,
      maxCompletedDepth,
      positionKeys,
      extremeTable,
    );
    const rankedMoves = Object.freeze(
      result.rankedMoves.map((entry) => Object.freeze({ ...entry })),
    );
    return Object.freeze({
      move: selectMove(rankedMoves, budget, seed),
      rankedMoves,
      nodes: result.nodes,
      completedDepth: result.completedDepth,
      exhaustedBudget: result.exhaustedBudget,
    });
  }

  const context: SearchContext = {
    perspective: state.activePlayer,
    nodeLimit: budget.nodeLimit,
    table: new BoundedTranspositionTable(budget.tableCapacity),
    repetitions: new RepetitionTracker(positionKeys),
    nodes: 0,
  };
  let completedDepth = 0;
  let exhaustedBudget = false;
  let rankedMoves: readonly ScoredMove[] = Object.freeze(
    legalMoves.map((move) =>
      Object.freeze({
        move,
        score: Math.min(HEURISTIC_SCORE_LIMIT, moveOrderingScore(state, move)),
        provenTerminal: null,
      }),
    ),
  );

  for (let depth = 1; depth <= budget.maxDepth; depth += 1) {
    try {
      rankedMoves = rankAtDepth(state, legalMoves, depth, context, budget.candidateCount === 1);
      completedDepth = depth;
    } catch (error) {
      if (!(error instanceof NodeBudgetExhausted)) throw error;
      exhaustedBudget = true;
      break;
    }
  }

  rankedMoves = Object.freeze([...rankedMoves].sort(compareScoredMoves));
  return Object.freeze({
    move: selectMove(rankedMoves, budget, seed),
    rankedMoves,
    nodes: context.nodes,
    completedDepth,
    exhaustedBudget,
  });
}
